import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import MuiAlert from '@material-ui/core/Alert';
import MuiFormControl from '@material-ui/core/FormControl';
import MuiInputLabel from '@material-ui/core/InputLabel';
import MuiFormHelperText from '@material-ui/core/FormHelperText';
import { motion, AnimatePresence } from 'framer-motion';
import FormFocus from '../../utils/FormFocus';
var useStyles = makeStyles(function (theme) {
  return {
    root: {
      pointerEvents: function pointerEvents(_ref) {
        var disabled = _ref.disabled;
        return disabled ? 'none' : null;
      },
      '& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after': {
        display: 'none'
      },
      color: function color(_ref2) {
        var _color = _ref2.color;
        if (_color === 'inherit') {
          return 'inherit';
        }
        return theme.palette.default.main;
      }
    },
    label: {
      fontSize: '13px',
      lineHeight: 0.9,
      fontWeight: 600,
      width: '100%',
      whiteSpace: 'normal',
      color: function color(_ref3) {
        var _color2 = _ref3.color;
        if (_color2 === 'inherit') {
          return 'inherit';
        }
        return theme.palette.text.primary;
      },
      transform: 'initial',
      transformOrigin: 'initial',
      overflow: 'initial',
      paddingBottom: '4px',
      marginBottom: function marginBottom(_ref4) {
        var helperText = _ref4.helperText,
          helperTextPlacement = _ref4.helperTextPlacement;
        return helperText && helperTextPlacement === 'above' ? 0 : theme.spacing(1);
      },
      position: 'relative',
      '&:after': {
        content: function content(_ref5) {
          var required = _ref5.required;
          return required ? "'*'" : null;
        },
        color: theme.palette.error.main
      }
    },
    helper: {
      order: function order(_ref6) {
        var helperTextPlacement = _ref6.helperTextPlacement;
        return helperTextPlacement === 'below' ? 1 : null;
      },
      marginBottom: theme.spacing(1),
      marginTop: function marginTop(_ref7) {
        var helperTextPlacement = _ref7.helperTextPlacement;
        return helperTextPlacement === 'below' ? theme.spacing(1) : 0;
      },
      '& svg': {
        verticalAlign: 'middle',
        height: '1.3em',
        width: '1.3em',
        position: 'relative',
        marginTop: '-0.15em',
        fill: theme.palette.secondary.main
      }
    },
    error: {
      color: theme.palette.secondary.contrastText,
      padding: theme.spacing(1),
      background: theme.palette.secondary.main,
      alignItems: 'center',
      marginTop: '2px',
      borderRadius: function borderRadius(_ref8) {
        var edges = _ref8.edges;
        switch (edges) {
          case 'rounded':
            return 99;
          case 'sharp':
            return 0;
          default:
            return theme.shape.borderRadius;
        }
      },
      '& *': {
        padding: 0,
        fontSize: theme.typography.size.scale(3.6),
        marginRight: theme.spacing(0.5)
      },
      '& svg': {
        color: theme.palette.secondary.contrastText,
        verticalAlign: 'middle',
        height: '1.3em',
        width: '1.3em',
        position: 'relative',
        marginTop: '-0.15em'
      }
    }
  };
});
var FormControl = function FormControl(_ref9) {
  var color = _ref9.color,
    disabled = _ref9.disabled,
    fullWidth = _ref9.fullWidth,
    helperText = _ref9.helperText,
    helperTextPlacement = _ref9.helperTextPlacement,
    label = _ref9.label,
    hideLabel = _ref9.hideLabel,
    required = _ref9.required,
    showError = _ref9.showError,
    errorMessage = _ref9.errorMessage,
    children = _ref9.children,
    edges = _ref9.edges,
    name = _ref9.name;
  var classes = useStyles({
    color: color,
    helperText: helperText,
    helperTextPlacement: helperTextPlacement,
    required: required,
    edges: edges,
    disabled: disabled
  });
  return /*#__PURE__*/React.createElement(MuiFormControl, {
    fullWidth: fullWidth,
    className: classes.root
  }, label && !hideLabel && /*#__PURE__*/React.createElement(MuiInputLabel, {
    shrink: true,
    htmlFor: name,
    className: classes.label
  }, label), helperText && /*#__PURE__*/React.createElement(MuiFormHelperText, {
    className: classes.helper
  }, /*#__PURE__*/React.createElement(InfoOutlinedIcon, {
    fontSize: "inherit"
  }), " ", helperText), children || /*#__PURE__*/React.createElement(FormFocus, {
    name: name
  }), /*#__PURE__*/React.createElement(AnimatePresence, null, showError && /*#__PURE__*/React.createElement(motion.div, {
    initial: {
      translateY: -20,
      opacity: 0
    },
    animate: {
      translateY: 0,
      opacity: 1
    },
    exit: {
      translateY: -20,
      opacity: 0
    },
    transition: {
      duration: 0.25,
      ease: 'easeOut'
    }
  }, /*#__PURE__*/React.createElement(MuiAlert, {
    severity: "error",
    className: classes.error
  }, errorMessage))));
};
FormControl.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * @ign
   */
  children: PropTypes.any,
  /**
   * Define color
   */
  color: PropTypes.oneOf(['default', 'inherit']),
  /**
   * If `true`, the label, input and helper text should be displayed in a disabled state.
   */
  disabled: PropTypes.bool,
  /**
   * Whether or not to display an error message (if there is a message to display).
   */
  showError: PropTypes.bool,
  /**
   * The error message to display if showError === true
   */
  errorMessage: PropTypes.string,
  /**
   * If `true`, the component will take up the full width of its container.
   */
  fullWidth: PropTypes.bool,
  /**
   * The helper text content.
   */
  helperText: PropTypes.node,
  /**
   * Placed the helper text above or below the input.
   */
  helperTextPlacement: PropTypes.oneOf(['above', 'below']),
  /**
   * The label content.
   */
  label: PropTypes.node,
  /**
   * Make label visible only to screen readers
   */
  hideLabel: PropTypes.bool,
  /**
   * The input name.
   */
  name: PropTypes.string.isRequired,
  /**
   * If `true`, the label will indicate that the input is required.
   */
  required: PropTypes.bool,
  /**
   * Make button edges rounded or square
   */
  edges: PropTypes.oneOf(['normal', 'rounded', 'sharp'])
} : {};
FormControl.defaultProps = {
  children: null,
  color: 'default',
  disabled: false,
  fullWidth: false,
  hideLabel: false,
  helperText: '',
  helperTextPlacement: 'above',
  label: '',
  required: false,
  showError: false,
  errorMessage: '',
  edges: 'normal'
};
export default FormControl;